import { Component, ElementRef, Input, OnInit, Renderer2 } from '@angular/core';
import { isNumber } from '../../models/utils';
import { CommonService } from '../../services/common/common.service';

@Component({
    standalone: false,
    selector: 'res-resizer',
    template: `<i class="fa" [ngClass]="getExpanderIconClass()"></i>
        <i title="{{collapsedTitle}}" class="fa collapsedIcon" [ngClass]="collapsedIcon" [hidden]="!collapsed"></i>
        <div class="inputArea" [hidden]="collapsed"></div>`
})
export class ResizerComponent implements OnInit {
    @Input() anchor: string;
    @Input() resizingElementId: string;
    @Input() secondElementId: string;
    @Input() thirdElementId: string;
    @Input() parentElementId: string;

    private isResizable = true;
    @Input()
    set resizable(resizable: boolean) {
        if (typeof resizable === 'boolean') {
            this.isResizable = resizable;
            this.setExpanderClasses();
        }
    }

    private _isCollapsed = false;
    @Input()
    set collapsed(collapsed: boolean) {
        if (typeof collapsed === 'boolean') {
            this._isCollapsed = collapsed;
            this.setExpanderClasses();
        }
    }
    get collapsed(): boolean {
        return this._isCollapsed;
    }

    private isCollapsible = false;
    @Input()
    set collapsible(collapsible: boolean) {
        if (typeof collapsible === 'boolean') {
            this.isCollapsible = collapsible;
        }
    }

    private isCollapsibleToMinSize = false;
    @Input()
    set collapsibleToMinSize(value: boolean) {
        if (typeof value === 'boolean') {
            this.isCollapsibleToMinSize = value;
        }
    }

    private isExpandableToMax = false;
    @Input()
    set expandableToMax(value: boolean) {
        if (typeof value === 'boolean') {
            this.isExpandableToMax = value;
        }
    }

    @Input() usePercentages: boolean;
    @Input() initSize: number;
    @Input() minSize: number;
    @Input() maxSize: number;
    @Input() marginToMaxParentSize: number;
    @Input() collapsedTitle: string;
    @Input() collapsedIcon: string;
    @Input() customClass: string;
    @Input() ellipsisExpander: boolean;
    @Input() wideInputOnDrag: boolean;
    @Input() resizeCallback: () => void;

    private currentSize: number;
    private resizingElement: HTMLElement;
    private secondElement: HTMLElement;
    private thirdElement: HTMLElement;
    private inputElement: HTMLElement;
    private parentElement: HTMLElement;
    alternativeMouseEventElement: HTMLElement;

    private isVertical = false;
    private isReversed = false;

    private mouseDownEvent: MouseEvent;
    private mouseMoveEvent: MouseEvent;

    constructor(private elementRef: ElementRef, private renderer: Renderer2, private commonService: CommonService) { }

    ngOnInit(): void {
        this.isReversed = this.anchor === 'right' || this.anchor === 'bottom';
        this.isVertical = this.anchor === 'left' || this.anchor === 'right';
        this.setExpanderClasses();
        if (this.resizingElementId) {
            this.resizingElement = document.getElementById(this.resizingElementId);
            if (this.resizingElement) {
                if (this.parentElementId) {
                    this.parentElement = document.getElementById(this.parentElementId);
                } else {
                    this.parentElement = this.resizingElement.parentElement;
                }
                if (this.usePercentages) {
                    setTimeout(() => {
                        if (!this.parentElement || (this.isVertical && !this.parentElement.clientWidth) || (!this.isVertical && !this.parentElement.clientHeight)) {
                            this.usePercentages = false;
                        }
                        if (this.usePercentages) {
                            this.initSize = ((this.isVertical ? this.parentElement.clientWidth : this.parentElement.clientHeight) * this.initSize) / 100;
                        }
                        if (this.collapsed) {
                            this.setElementSize(0);
                        } else {
                            this.setElementSize(this.initSize);
                        }
                    }, 0);
                } else {
                    if (this.collapsed) {
                        this.setElementSize(0);
                    } else {
                        this.setElementSize(this.initSize);
                    }
                }
                this.elementRef.nativeElement.addEventListener('mousedown', this.onMouseDown);
            }
        }
        if (this.secondElementId) {
            this.secondElement = document.getElementById(this.secondElementId);
        }

        if (this.wideInputOnDrag) {
            this.inputElement = Array.from<HTMLElement>(this.elementRef.nativeElement.children).find((c) => c.className.includes('inputArea'));
        }
    }

    onMouseUp = (event?: MouseEvent): void => {
        // check if simple click
        if (event && (this.isCollapsible || this.isCollapsibleToMinSize || this.isExpandableToMax)
            && this.isResizable && this.mouseDownEvent && (event.timeStamp - this.mouseDownEvent.timeStamp < 200)
            && (Math.abs((this.mouseDownEvent.screenX - event.screenX)) < 5 && Math.abs((this.mouseDownEvent.screenY - event.screenY)) < 5)) {
            this.toggleCollapse();
        }
        this.mouseDownEvent = null;
        this.mouseMoveEvent = null;
        document.removeEventListener('mousemove', this.onMouseMove);
        document.removeEventListener('mouseup', this.onMouseUp);
        if (this.wideInputOnDrag && this.inputElement) {
            this.renderer.setStyle(this.inputElement, this.isVertical ? 'width' : 'height', '20px');
            this.renderer.setStyle(this.inputElement, this.isVertical ? 'left' : 'top', '-6px');
        }
        if (this.alternativeMouseEventElement) {
            this.alternativeMouseEventElement.removeEventListener('mousemove', this.onMouseMove);
            this.alternativeMouseEventElement.removeEventListener('mouseup', this.onMouseUp);
        }
    }

    onMouseMove = (event: MouseEvent): void => {
        if (this.mouseMoveEvent && this.resizingElement && this.isResizable && !this.collapsed) {
            let size = this.isVertical ? this.resizingElement.clientWidth : this.resizingElement.clientHeight;
            const distance = this.isVertical ? event.screenX - this.mouseMoveEvent.screenX : event.screenY - this.mouseMoveEvent.screenY;
            size += this.isReversed ? -distance : distance;
            this.setElementSize(size);
            this.mouseMoveEvent = event;
            if (window.getSelection) {
                window.getSelection().removeAllRanges();
            } else if (document.getSelection) {
                document.getSelection().removeAllRanges();
            }
        }
    }

    onMouseDown = (event: MouseEvent): void => {
        if (this.resizingElement) {
            this.mouseDownEvent = event;
            this.mouseMoveEvent = event;
            document.addEventListener('mousemove', this.onMouseMove);
            document.addEventListener('mouseup', this.onMouseUp);
            if (this.wideInputOnDrag && this.inputElement) {
                this.renderer.setStyle(this.inputElement, this.isVertical ? 'width' : 'height', (this.isVertical ? window.innerWidth * 2 : window.innerHeight * 2) + 'px');
                this.renderer.setStyle(this.inputElement, this.isVertical ? 'left' : 'top', -(this.isVertical ? window.innerWidth : window.innerHeight) + 'px');
            }
            if (this.alternativeMouseEventElement) {
                this.alternativeMouseEventElement.addEventListener('mousemove', this.onMouseMove);
                this.alternativeMouseEventElement.addEventListener('mouseup', this.onMouseUp);
            }
        }
    }

    toggleCollapse(): void {
        this.collapsed = !this.collapsed;
        const valueToCollapse = this.isCollapsibleToMinSize ? this.minSize : 0;
        this.setElementSize(this.collapsed ? valueToCollapse : (this.isExpandableToMax ? this.maxSize : this.initSize));
    }

    setElementSize(size: number): void {
        if (isNumber(size)) {
            if (size < 15 && !this.collapsed && this.isCollapsible) {
                size = 0;
                this.collapsed = true;
                this.onMouseUp();
            }
            if (this.usePercentages) {
                size = (size / (this.isVertical ? this.parentElement.clientWidth : this.parentElement.clientHeight)) * 100;
            }
            if (isNumber(this.maxSize)) {
                size = Math.min(size, this.maxSize);
            } else {
                if (this.marginToMaxParentSize && this.parentElement) {
                    size = Math.min(size, this.parentElement.clientWidth - this.marginToMaxParentSize);
                }
            }
            if (isNumber(this.minSize) && !this.collapsed) {
                size = Math.max(size, this.minSize);
            }

            if (this.thirdElementId) {
                this.thirdElement = document.getElementById(this.thirdElementId);
            }

            if (this.isVertical && this.thirdElementId) {
                const allowLeftResize = (!this.secondElement ||
                    (size + (this.anchor === 'left' ? 20 : 0) <= this.parentElement.clientWidth - this.thirdElement?.clientWidth -
                        (this.thirdElement?.clientWidth === 0 ? 20 : 0)));

                if (allowLeftResize) {
                    this.updateResizing(size);
                }
            } else {
                this.updateResizing(size);
            }
        }
    }

    updateResizing(size) {
        if (this.currentSize !== size) {
            this.currentSize = size;
            if (this.isVertical) {
                this.resizingElement.style.width = `${size}${this.usePercentages ? '%' : 'px'}`;
            } else {
                this.resizingElement.style.height = `${size}${this.usePercentages ? '%' : 'px'}`;
            }
            if (this.commonService.currentBrowserIsIE) {
                const evt = document.createEvent('UIEvents');
                evt.initEvent('resizerResize', true, false);
                window.dispatchEvent(evt);
            } else {
                window.dispatchEvent(new Event('resizerResize'));
            }

            if (this.resizeCallback) {
                this.resizeCallback();
            }
        }
    }

    setExpanderClasses(): void {
        this.renderer.setAttribute(this.elementRef.nativeElement, 'class',
            (this.isVertical ? ' vertical' : ' horizontal') +
            (this.collapsed ? ' collapsed' : '') +
            ` ${this.anchor}` +
            (this.isResizable ? ' resizable' : '') +
            (this.isReversed ? ' reversed' : '') +
            (this.customClass ? ` ${this.customClass}` : ''));
    }

    getExpanderIconClass(): string {
        if (this.anchor === 'left') {
            return this.collapsed ? 'fa-caret-right' : (this.ellipsisExpander ? 'fa-ellipsis-v' : 'fa-caret-left');
        } else if (this.anchor === 'right') {
            return this.collapsed ? 'fa-caret-left' : (this.ellipsisExpander ? 'fa-ellipsis-v' : 'fa-caret-right');
        } else if (this.anchor === 'up') {
            return this.collapsed ? 'fa-caret-down' : (this.ellipsisExpander ? 'fa-ellipsis-h' : 'fa-caret-up');
        } else if (this.anchor === 'bottom') {
            return this.collapsed ? 'fa-caret-up' : (this.ellipsisExpander ? 'fa-ellipsis-h' : 'fa-caret-down');
        }
    }
}
